<template>
  <v-container fluid class="persons-page ds-page">
    <!-- Filtros e Controles: uma barra só (antes, quatro campos soltos de 280px). -->
    <div class="pp-toolbar mt-5">
      <!--
        SALVIA-945: várias clínicas de uma vez — digita-se o nome, marcam-se
        várias (ou todas) e a lista junta sem repetir quem é atendido em mais
        de uma. Mesmo seletor da tela de novo atendimento.
      -->
      <v-autocomplete
        v-if="clinics.length"
        v-model="selectedClinics"
        :items="clinics"
        item-text="name"
        item-value="id"
        multiple
        outlined
        dense
        hide-details
        color="primary"
        prepend-inner-icon="mdi-hospital-building"
        label="Clínicas"
        no-data-text="Nenhuma clínica com esse nome"
        :menu-props="{ offsetY: true, maxHeight: 300 }"
        class="pp-field pp-field--clinicas"
        data-tour="pp-clinic"
        @change="handleClinicSelection"
      >
        <template v-if="clinics.length > 1" v-slot:prepend-item>
          <v-list-item ripple @mousedown.prevent @click="toggleAllClinics">
            <v-list-item-action>
              <v-icon :color="allClinicsSelected ? 'primary' : ''">{{ allClinicsIcon }}</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>Todas as clínicas</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-divider class="mt-1" />
        </template>
        <template v-slot:selection="{ item, index }">
          <span v-if="index === 0" class="pp-clinicas__primeira">
            {{ allClinicsSelected && clinics.length > 1 ? "Todas as clínicas" : item.name }}
          </span>
          <span
            v-else-if="index === 1 && !allClinicsSelected"
            class="pp-clinicas__mais"
          >+{{ selectedClinics.length - 1 }}</span>
        </template>
      </v-autocomplete>

      <!-- Modo de Visualização (Médicos/Pacientes) -->
      <v-select
        v-if="userType === 'assistant' && selectedClinics.length"
        v-model="viewMode"
        :items="viewModeOptions"
        label="Visualizar"
        prepend-inner-icon="mdi-account"
        outlined
        dense
        hide-details
        color="primary"
        class="pp-field pp-field--modo"
        data-tour="pp-viewmode"
        @change="handleViewModeChange"
      />

      <!-- Campo de Pesquisa -->
      <v-text-field
        v-model="searchQuery"
        :label="searchLabel"
        prepend-inner-icon="mdi-magnify"
        clearable
        outlined
        dense
        hide-details
        color="primary"
        class="pp-field pp-field--busca"
        data-tour="pp-search"
        @input="filterItems"
      />

      <!-- Ordenação dos cards -->
      <v-select
        v-model="orderBy"
        :items="orderOptions"
        label="Ordenar por"
        prepend-inner-icon="mdi-sort"
        outlined
        dense
        hide-details
        color="primary"
        class="pp-field pp-field--ordem"
        data-tour="pp-order"
        @change="onOrderChange"
      />

      <!-- SALVIA-955: troca a lista pelos pacientes que o médico ocultou. -->
      <v-btn
        v-if="canHidePatients && (hiddenCount || showHidden)"
        :color="showHidden ? 'primary' : undefined"
        :outlined="!showHidden"
        depressed
        height="40"
        class="pp-hidden-toggle"
        @click="toggleShowHidden"
      >
        <v-icon left>{{ showHidden ? "mdi-eye-outline" : "mdi-eye-off-outline" }}</v-icon>
        {{ showHidden ? "Voltar à lista" : `Ocultos (${hiddenCount})` }}
      </v-btn>

      <!-- Botão Adicionar -->
      <v-btn
        v-if="showAddButton"
        color="primary"
        depressed
        height="40"
        class="pp-add"
        data-tour="pp-add"
        @click="handleAddNewUser"
      >
        <v-icon left>mdi-plus</v-icon>
        {{ addButtonLabel }}
      </v-btn>
    </div>

    <!-- Contador de resultados -->
    <div v-if="!loading" class="pp-summary">
      <strong>{{ filteredItems.length }}</strong>
      {{ resultLabel }}<template v-if="showHidden"> {{ filteredItems.length === 1 ? "oculto" : "ocultos" }}</template>
      <span v-if="searchQuery && filteredItems.length !== listPool.length">
        de {{ listPool.length }}
      </span>
    </div>

    <!-- Estado de Carregamento -->
    <v-row v-if="loading" class="justify-center align-center my-10">
      <v-col cols="12" class="text-center">
        <v-progress-circular indeterminate color="primary" size="70" />
        <p class="mt-3 loading-text">Carregando...</p>
      </v-col>
    </v-row>

    <!-- Estado Vazio -->
    <v-row v-else-if="filteredItems.length === 0" class="justify-center my-4">
      <v-col cols="12" md="8" lg="6">
        <v-card class="empty-state-card pa-6 text-center">
          <v-icon size="56" color="#C2DDD3" class="mb-3">
            mdi-account-search-outline
          </v-icon>
          <h3 class="empty-state-title mb-2">
            {{ emptyStateTitle }}
          </h3>
          <p class="empty-state-subtitle mb-0">
            {{ emptyStateSubtitle }}
          </p>
        </v-card>
      </v-col>
    </v-row>

    <!-- Grid de Cards -->
    <v-row v-else>
      <v-col
        v-for="(item, pi) in filteredItems"
        :key="item.id"
        cols="12"
        sm="6"
        md="3"
        :data-tour="pi === 0 ? 'pp-card' : null"
        :data-person-id="item.id"
        class="d-flex"
      >
        <PersonCard
          :item="item"
          :next-appointment="nextApptByPatient[item.id] || null"
          :highlighted="item.id === highlightedId"
          :card-type="cardType"
          :user-type="userType"
          :can-hide="canHidePatients"
          @toggle-hidden="togglePatientHidden"
          @open-dialog="handleOpenDialog"
          @schedule-appointment="handleScheduleAppointment"
          @updatePersonsPageFromPersonCard="reloadList"
          @edit-person="handleEditPerson"
          @add-dependent="handleAddDependent"
        />
      </v-col>
    </v-row>

    <!-- Diálogos existentes -->
    <DoctorPatientsDialog
      v-if="dialogType === 'pacientes-do-medico'"
      :key="'doctor-' + dialogData?.id"
      v-model="dialogVisible"
      :item="dialogData"
    />

    <PatientDoctorsDialog
      v-if="dialogType === 'medicos-do-paciente'"
      :key="'patient-' + dialogData?.id"
      v-model="dialogVisible"
      :item="dialogData"
    />

    <DependentsDialog
      v-if="dialogType === 'dependentes-do-responsavel'"
      :key="'dependents-' + dialogData?.id"
      v-model="dialogVisible"
      :item="dialogData"
      @refresh="reloadList"
    />

    <AddNewUserDialog
      v-model="addNewUserDialog.visible"
      :user-id="addNewUserDialog.userId"
      :target-type="addNewUserDialog.targetType"
      :external-guardian-id="addNewUserDialog.guardianId"
      :external-guardian-address="addNewUserDialog.guardianAddress"
      @updatePersonsPage="onPersonSaved"
    />

    <CalendarDialogAddNewEvent
      v-model="openDialogAddNewEvent"
      :preSelectedPatient="AddNewEventPreSelectedPatient"
    />
  </v-container>
</template>

<style scoped>
.persons-page {
  min-height: 100vh;
}

.persons-page :deep(.v-text-field.v-input--is-focused fieldset),
.persons-page :deep(.v-select.v-input--is-focused fieldset) {
  border-color: var(--ds-primary) !important;
}

/* Inputs e selects na cor do profile-hero */
.persons-page :deep(.v-text-field .v-input__slot),
.persons-page :deep(.v-select .v-input__slot),
.persons-page :deep(.v-autocomplete .v-input__slot) {
  background-color: var(--ds-blue-50) !important;
}

/* ═══ Barra de filtros (uma linha, moldura dos cartões) ═══ */
.pp-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: var(--ds-surface-white);
  border: 1px solid var(--ds-blue-100);
  border-radius: 8px;
}
.pp-field {
  flex: 0 1 220px;
  min-width: 180px;
}
.pp-field--clinicas {
  flex-basis: 260px;
}
.pp-field--busca {
  flex: 1 1 240px;
}
.pp-hidden-toggle,
.pp-add {
  flex: 0 0 auto;
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
}
.pp-clinicas__primeira {
  max-width: 70%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pp-clinicas__mais {
  margin-left: 6px;
  padding: 0 7px;
  border-radius: 999px;
  background: var(--ds-primary-surface);
  color: var(--ds-primary);
  font-size: 0.72rem;
  font-weight: 700;
}
.pp-summary {
  margin: 10px 2px 4px;
  font-size: 13px;
  color: var(--ds-text-soft);
}
.pp-summary strong {
  color: var(--ds-primary);
}
@media (max-width: 700px) {
  .pp-field,
  .pp-add {
    flex: 1 1 100%;
  }
}

.loading-text {
  font-size: 13px;
  color: var(--ds-text-soft);
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-weight: 600;
}

.empty-state-card {
  border-radius: 16px !important;
  border: 1.5px dashed var(--ds-blue-200) !important;
  background: var(--ds-blue-50) !important;
  box-shadow: none !important;
}

.empty-state-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--ds-primary);
  letter-spacing: -0.2px;
}

.empty-state-subtitle {
  font-size: 13px;
  color: var(--ds-text-soft);
  font-weight: 500;
  line-height: 1.5;
}
</style>

<script>
import http from "@/services/http";
import { useSnackbar } from "@/composables/useSnackbar";
import { emitPatientListChanged } from "@/services/patientListBus";

import PersonCard from "@/features/patients/persons/PersonCard.vue";
import PatientDoctorsDialog from "@/features/patients/persons/PatientDoctorsDialog.vue";
import DoctorPatientsDialog from "@/features/patients/persons/DoctorPatientsDialog.vue";
import DependentsDialog from "@/features/patients/persons/DependentsDialog.vue";
import AddNewUserDialog from "@/features/patients/add-user/AddNewUserDialog.vue";
import CalendarDialogAddNewEvent from "@/features/calendar/components/CalendarDialogAddNewEvent.vue";
import tourMixin from "@/features/tour/tourMixin";

import {
  getMyClinics,
  getDoctorsByClinics,
  getPatientsByClinics,
} from "@/services/clinicService.js";
import { calculateAge } from "@/utils/calculateAge.js";
import { formatBirthDate } from "@/utils/formatBirthDate.js";

const VIEW_MODES = {
  DOCTORS: "medicos",
  PATIENTS: "pacientes",
};

const USER_TYPES = {
  SOLO_DOCTOR: "doctor_solo",
  CLINIC_DOCTOR: "doctor_clinic",
  ASSISTANT: "assistant",
};

export default {
  name: "PersonsPage",

  mixins: [tourMixin],

  components: {
    PersonCard,
    PatientDoctorsDialog,
    DoctorPatientsDialog,
    DependentsDialog,
    AddNewUserDialog,
    CalendarDialogAddNewEvent,
  },

  setup() {
    const { success, erroDeRequisicao } = useSnackbar();
    return { success, erroDeRequisicao };
  },

  data() {
    return {
      // Estado do usuário
      userType: null,
      userId: null,
      // Dados das clínicas
      clinics: [],
      // SALVIA-945: várias clínicas (antes, uma só).
      selectedClinics: [],
      // Recém-cadastrado: o card pisca duas vezes.
      highlightedId: null,
      // Dados dos usuários
      doctors: [],
      patients: [],
      filteredItems: [],
      // Estados da interface
      loading: true,
      searchQuery: "",
      viewMode: null,
      // Ordenação da listagem ("alpha" | "appointment")
      orderBy: "alpha",
      // SALVIA-955: mostrando os pacientes ocultos em vez dos visíveis.
      showHidden: false,
      // Mapa patientId -> data/hora (ISO) do próximo agendamento
      nextApptByPatient: {},
      // Estado dos diálogos
      dialogData: null,
      dialogType: null,
      dialogVisible: false,
      openDialogAddNewEvent: false,
      AddNewEventPreSelectedPatient: null,
      addNewUserDialog: {
        visible: false,
        userId: null,
        targetType: "patient",
        guardianId: null,
        guardianAddress: null,
      },
    };
  },

  computed: {
    tourId() {
      return "pacientes";
    },
    viewModeOptions() {
      return [VIEW_MODES.DOCTORS, VIEW_MODES.PATIENTS];
    },

    orderOptions() {
      return [
        { text: "Ordem alfabética", value: "alpha" },
        { text: "Próximo agendamento", value: "appointment" },
      ];
    },

    showAddButton() {
      const doctorTypes = [USER_TYPES.SOLO_DOCTOR, USER_TYPES.CLINIC_DOCTOR];
      if (doctorTypes.includes(this.userType)) return true;

      // Assistente só adiciona PACIENTE (botão de adicionar médico removido).
      if (this.userType === USER_TYPES.ASSISTANT) {
        return this.viewMode === VIEW_MODES.PATIENTS;
      }

      return false;
    },

    allClinicsSelected() {
      return this.clinics.length > 0 && this.selectedClinics.length === this.clinics.length;
    },
    allClinicsIcon() {
      if (this.allClinicsSelected) return "mdi-checkbox-marked";
      return this.selectedClinics.length ? "mdi-minus-box" : "mdi-checkbox-blank-outline";
    },
    /** SALVIA-955: médico (solo ou de clínica) olhando a lista de pacientes. */
    canHidePatients() {
      return (
        [USER_TYPES.SOLO_DOCTOR, USER_TYPES.CLINIC_DOCTOR].includes(this.userType) &&
        this.cardType === "paciente"
      );
    },
    hiddenCount() {
      return this.patients.filter((p) => p.hidden).length;
    },
    /** A lista em exibição antes da busca: visíveis ou só os ocultos. */
    listPool() {
      if (!this.canHidePatients) return this.patients;
      return this.patients.filter((p) => Boolean(p.hidden) === this.showHidden);
    },

    resultLabel() {
      const one = this.filteredItems.length === 1;
      if (this.viewMode === VIEW_MODES.DOCTORS) return one ? "médico" : "médicos";
      return one ? "paciente" : "pacientes";
    },

    addButtonLabel() {
      return "Adicionar Paciente";
    },

    searchLabel() {
      const type = this.viewMode === VIEW_MODES.DOCTORS ? "Médico" : "Paciente";
      return `Pesquisar ${type}`;
    },

    cardType() {
      return this.viewMode === VIEW_MODES.DOCTORS ? "medico" : "paciente";
    },

    /**
     * Por que a lista está vazia? A mensagem dependia só do TIPO de usuário: o
     * médico de clínica via "Você ainda não tem uma clínica" mesmo com a
     * clínica selecionada no filtro ao lado. Agora segue a situação real.
     */
    motivoDaListaVazia() {
      const ehClinica = [USER_TYPES.CLINIC_DOCTOR, USER_TYPES.ASSISTANT].includes(this.userType);

      if ((this.searchQuery || "").trim()) return "busca";
      if (ehClinica && !this.clinics.length) return "sem-clinica";
      return "sem-cadastro";
    },

    emptyStateTitle() {
      if (this.showHidden && !(this.searchQuery || "").trim()) return "Nenhum paciente oculto.";
      const quem = this.viewMode === VIEW_MODES.DOCTORS ? "médicos" : "pacientes";
      return {
        busca: "Nenhum resultado para a sua busca.",
        "sem-clinica": "Você ainda não tem uma clínica.",
        "sem-cadastro": this.clinics.length
          ? `Nenhum ${quem.slice(0, -1)} ${this.selectedClinics.length > 1 ? "nas clínicas escolhidas" : "nesta clínica"} ainda.`
          : `Você ainda não tem ${quem}.`,
      }[this.motivoDaListaVazia];
    },

    emptyStateSubtitle() {
      if (this.showHidden && !(this.searchQuery || "").trim()) {
        return "Pacientes ocultados pelo menu ⋮ do card aparecem aqui.";
      }
      return {
        busca: "Confira a grafia ou tente outro termo.",
        "sem-clinica": "Adicione uma clínica para começar a atender pacientes.",
        "sem-cadastro": "Use o botão de adicionar para cadastrar o primeiro.",
      }[this.motivoDaListaVazia];
    },
  },

  watch: {
    patients: {
      handler: "filterItems",
      immediate: true,
    },
    searchQuery: "filterItems",
    showHidden: "filterItems",
  },

  async mounted() {
    await this.initializeComponent();
  },

  methods: {
    // === TOUR GUIADO (tourMixin fornece auto-start + botão "?") ===
    tourReady() {
      return !this.loading; // espera clínicas/pessoas carregarem
    },
    tourSteps() {
      const isAssistant = this.userType === USER_TYPES.ASSISTANT;
      const steps = [
        {
          // Boas-vindas (sem âncora → card centralizado).
          title: "Pessoas 👥",
          description:
            "Esta página reúne as pessoas ligadas às suas clínicas — pacientes e, para assistentes, também os médicos. Aqui você busca, organiza e abre o perfil de cada uma. Reabra este tour quando quiser pelo botão “?”.",
        },
      ];

      steps.push({
        anchor: "pp-clinic",
        title: "Selecione a clínica",
        description:
          "Se você atende em mais de uma clínica, escolha aqui de qual delas quer ver as pessoas. A lista abaixo é atualizada conforme a clínica escolhida.",
      });

      if (isAssistant) {
        steps.push({
          anchor: "pp-viewmode",
          title: "Médicos ou Pacientes",
          description:
            "Como assistente, você alterna aqui entre ver os médicos da clínica ou os pacientes dela. O restante da página (busca, ordenação e cards) acompanha essa escolha.",
        });
      }

      steps.push(
        {
          anchor: "pp-search",
          title: "Busque pelo nome",
          description:
            "Digite parte do nome para filtrar a lista em tempo real — prático quando há muitas pessoas cadastradas. Combine com o filtro de clínica acima para chegar rápido a quem procura.",
        },
        {
          anchor: "pp-order",
          title: "Ordene a lista",
          description:
            "Escolha a ordem dos cards: alfabética (para localizar por nome) ou por próximo agendamento (para ver primeiro quem será atendido em seguida). A ordenação vale para a lista inteira.",
        },
        {
          anchor: "pp-add",
          title: "Adicione uma pessoa",
          description:
            "Cadastre um novo paciente ou vincule um já existente pelo CPF. O cadastro abre em um assistente de 2 passos (que tem o próprio tour). O texto do botão muda conforme o que você está visualizando (paciente ou médico).",
        },
        {
          anchor: "pp-card",
          title: "Cada pessoa é um card",
          description:
            "Cada card resume uma pessoa: foto, nome, idade e informações rápidas como gênero, nascimento e tipo sanguíneo. É o ponto de partida para tudo que você faz com ela.",
        },
        {
          anchor: "pp-card-actions",
          title: "Acessar e Agendar",
          description:
            "No rodapé do card, “Acessar” abre o perfil completo da pessoa (histórico, anamneses, medicamentos, metas etc.) e “Agendar” abre o formulário de novo atendimento já com ela pré-selecionada.",
        },
        {
          anchor: "pp-card-menu",
          title: "Mais opções",
          description:
            "Neste menu de três pontos ficam as ações extras do card: ver detalhes, editar os dados e — para pacientes — gerenciar dependentes (ver e adicionar). Para assistentes, também aparecem os vínculos “Médicos do paciente” / “Pacientes do médico”.",
        },
      );

      return steps;
    },

    // === INICIALIZAÇÃO ===
    async initializeComponent() {
      try {
        // ✅ garante que auth.user está carregado
        await this.$store.dispatch("auth/ensureAuth");

        // ✅ pega tudo do Vuex
        this.userType = this.$store.getters["auth/userType"];
        this.userId = this.$store.getters["auth/userId"];

        // Restaura a ordenação escolhida por este usuário (persistida localmente).
        const savedOrder = localStorage.getItem(this.orderStorageKey());
        if (savedOrder === "alpha" || savedOrder === "appointment") {
          this.orderBy = savedOrder;
        }

        await this.loadInitialData();
      } catch (error) {
        console.error("Erro na inicialização:", error);
        this.handleError("Erro ao carregar dados iniciais");
      }
    },

    async loadInitialData() {
      if (this.userType === USER_TYPES.SOLO_DOCTOR) {
        await this.loadSoloDoctorPatients();
      } else if (this.isClinicUser()) {
        await this.loadClinicData();
      }
    },

    async loadSoloDoctorPatients() {
      await this.getMyPatients(this.userId);
    },

    async loadClinicData() {
      this.clinics = await getMyClinics();

      if (this.clinics.length > 0) {
        this.selectedClinics = [this.clinics[0].id];
        await this.handleClinicSelection();

        if (this.userType === USER_TYPES.ASSISTANT) {
          this.viewMode = VIEW_MODES.DOCTORS;
          await this.handleViewModeChange();
        }
      }
    },

    handleEditPerson({ userId, targetType }) {
      this.addNewUserDialog.visible = true;
      this.addNewUserDialog.userId = userId;
      this.addNewUserDialog.targetType = targetType;
      // Não é fluxo de dependente — limpa qualquer responsável remanescente.
      this.addNewUserDialog.guardianId = null;
      this.addNewUserDialog.guardianAddress = null;
    },

    // === MANIPULADORES DE EVENTOS ===
    async handleClinicSelection() {
      if (!this.selectedClinics.length) {
        this.clearData();
        return;
      }

      if (this.userType === USER_TYPES.CLINIC_DOCTOR) {
        await this.getPatientsByClinic(this.selectedClinics);
      } else if (this.userType === USER_TYPES.ASSISTANT && this.viewMode) {
        await this.handleViewModeChange();
      }
    },

    /** "Todas as clínicas": marca todas; se já estavam todas, volta à primeira. */
    toggleAllClinics() {
      this.selectedClinics = this.allClinicsSelected
        ? [this.clinics[0].id]
        : this.clinics.map((c) => c.id);
      this.handleClinicSelection();
    },

    async handleViewModeChange() {
      if (!this.selectedClinics.length || !this.viewMode) return;

      this.setLoading(true);
      try {
        const data = await this.fetchDataByViewMode();
        this.patients = this.formatUsersData(data, this.getDataType());
        this.filteredItems = this.patients;
        await this.refreshOrdering();
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
        this.clearData();
      } finally {
        this.setLoading(false);
      }
    },

    /** Recarrega a lista mantendo as clínicas e o modo escolhidos. */
    async reloadList() {
      if (this.userType === USER_TYPES.SOLO_DOCTOR) {
        await this.getMyPatients(this.userId);
      } else {
        await this.handleClinicSelection();
      }
    },

    /**
     * Voltou do cadastro: recarrega a lista (sem perder a seleção de clínicas)
     * e, quando é um paciente novo ou recém-vinculado, pisca o card e rola até ele.
     */
    async onPersonSaved(payload) {
      emitPatientListChanged();
      await this.reloadList();
      const id = payload && payload.personId;
      if (!id) return;
      this.highlightedId = null;
      this.$nextTick(() => {
        this.highlightedId = id;
        this.$nextTick(() => {
          const el = this.$el.querySelector(`[data-person-id="${id}"]`);
          el?.scrollIntoView({ block: "center", behavior: "smooth" });
        });
        clearTimeout(this._highlightTimer);
        this._highlightTimer = setTimeout(() => { this.highlightedId = null; }, 1800);
      });
    },

    // Recarrega o mapa de agendamentos quando necessário e reaplica a ordenação.
    async refreshOrdering() {
      // A lista de pacientes sempre busca: o card mostra a próxima consulta.
      if (this.cardType === "paciente") {
        await this.fetchUpcomingEvents();
      } else {
        this.nextApptByPatient = {};
      }
      this.filterItems();
    },
    toggleShowHidden() {
      this.showHidden = !this.showHidden;
    },

    /**
     * SALVIA-955: oculta (com confirmação) ou reexibe o paciente na lista do
     * médico. Atualiza o card na hora, sem recarregar a página.
     */
    async togglePatientHidden(item) {
      const hide = !item.hidden;
      if (hide) {
        const ok = await this.$confirm({
          type: "warning",
          title: "Ocultar paciente?",
          message:
            `${item.name} sai da sua lista de pacientes e do novo atendimento. ` +
            "Nada é apagado: o prontuário continua, outros profissionais seguem vendo, " +
            "e você pode reexibir em \"Ocultos\".",
          confirmText: "Ocultar",
        });
        if (!ok) return;
      }

      try {
        await http.post(`/api/relation/patients/${item.id}/visibility`, { hidden: hide });
        const target = this.patients.find((p) => p.id === item.id);
        if (target) this.$set(target, "hidden", hide);
        if (!hide && this.showHidden && !this.hiddenCount) this.showHidden = false;
        this.filterItems();
        emitPatientListChanged(); // o Novo atendimento recarrega ao abrir
        this.success(hide ? "Paciente ocultado. Ele fica em \"Ocultos\"." : "Paciente de volta à sua lista.");
      } catch (err) {
        this.erroDeRequisicao(err, hide ? "Não foi possível ocultar o paciente" : "Não foi possível reexibir o paciente");
      }
    },

    handleAddNewUser() {
      this.addNewUserDialog.visible = true;
      this.addNewUserDialog.userId = null;
      this.addNewUserDialog.targetType = "patient";
      // Não é fluxo de dependente — limpa qualquer responsável remanescente.
      this.addNewUserDialog.guardianId = null;
      this.addNewUserDialog.guardianAddress = null;
    },
    handleOpenDialog({ type, data }) {
      this.dialogType = type;
      this.dialogData = data;
      this.dialogVisible = true;
    },
    // === FILTRAGEM, BUSCA E ORDENAÇÃO ===
    filterItems() {
      const query = this.searchQuery?.toLowerCase().trim() || "";
      const base = query
        ? this.listPool.filter((item) =>
            item.name.toLowerCase().includes(query),
          )
        : this.listPool;
      this.filteredItems = this.sortItems(base);
    },

    sortItems(list) {
      const arr = [...list];
      const byName = (a, b) =>
        (a.name || "").localeCompare(b.name || "", "pt-BR", {
          sensitivity: "base",
        });

      if (this.orderBy === "appointment") {
        // Próximo agendamento (mais cedo) primeiro; sem agendamento vai ao fim
        // (e entre os "sem", ordem alfabética).
        arr.sort((a, b) => {
          const ta = this.nextApptTime(a.id);
          const tb = this.nextApptTime(b.id);
          if (ta !== tb) return ta - tb;
          return byName(a, b);
        });
      } else {
        arr.sort(byName);
      }
      return arr;
    },

    nextApptTime(patientId) {
      const iso = this.nextApptByPatient[patientId];
      const t = iso ? new Date(iso).getTime() : NaN;
      return Number.isFinite(t) ? t : Infinity;
    },

    orderStorageKey() {
      return `personsOrderBy:${this.userId || "anon"}`;
    },

    async onOrderChange() {
      // Persiste a preferência de ordenação por usuário.
      try {
        localStorage.setItem(this.orderStorageKey(), this.orderBy);
      } catch (e) {
        // localStorage indisponível (modo privado etc.) — ignora.
      }
      // Só busca agendamentos quando necessário (e ainda não carregados).
      if (
        this.orderBy === "appointment" &&
        !Object.keys(this.nextApptByPatient).length
      ) {
        await this.fetchUpcomingEvents();
      }
      this.filterItems();
    },

    async fetchUpcomingEvents() {
      try {
        // Assistente vê a agenda por clínica: uma chamada por clínica marcada.
        const paramsList =
          this.userType === USER_TYPES.ASSISTANT && this.selectedClinics.length
            ? this.selectedClinics.map((id) => ({ clinic_id: id }))
            : [{}];
        const responses = await Promise.all(
          paramsList.map((params) => http.get("/api/events/upcoming", { params })),
        );
        const map = {};
        for (const ev of responses.flatMap((r) => r?.data?.data || [])) {
          if (!ev.patient_id || !ev.start) continue;
          const current = map[ev.patient_id];
          if (!current || new Date(ev.start) < new Date(current)) {
            map[ev.patient_id] = ev.start;
          }
        }
        this.nextApptByPatient = map;
      } catch (error) {
        console.error("Erro ao buscar próximos agendamentos:", error);
        this.nextApptByPatient = {};
      }
    },
    // === REQUISIÇÕES DE DADOS ===
    /** Junta as listas das clínicas marcadas, sem repetir pessoa. */
    mergeById(lists) {
      const byId = new Map();
      lists.flat().filter(Boolean).forEach((p) => byId.set(p.id, p));
      return [...byId.values()];
    },

    async fetchDataByViewMode() {
      const fetchOne = this.viewMode === VIEW_MODES.DOCTORS ? getDoctorsByClinics : getPatientsByClinics;
      const lists = await Promise.all(
        this.selectedClinics.map((id) => fetchOne(id).catch(() => [])),
      );
      return this.mergeById(lists);
    },
    async getMyPatients(userId) {
      this.setLoading(true);
      try {
        // ✅ http no lugar de fetch + localStorage + process.env
        const { data } = await http.get(
          `/api/relation/patients-by-doctor/${userId}`,
        );
        this.patients = this.formatUsersData(data.data, "paciente");
        this.filteredItems = this.patients;
        await this.refreshOrdering();
      } catch (error) {
        console.error("Erro ao buscar pacientes:", error);
        this.clearData();
      } finally {
        this.setLoading(false);
      }
    },
    async getPatientsByClinic(clinicIds) {
      if (!this.userId) return;
      this.setLoading(true);
      try {
        const ids = Array.isArray(clinicIds) ? clinicIds : [clinicIds];
        const lists = await Promise.all(
          ids.map((clinicId) =>
            http
              .get(`/api/clinic/${clinicId}/doctor/${this.userId}/get-patients-by-clinic`)
              .then(({ data }) => data?.data || [])
              .catch(() => []),
          ),
        );
        this.patients = this.formatUsersData(this.mergeById(lists), "paciente");
        this.filteredItems = this.patients;
        await this.refreshOrdering();
      } catch (error) {
        console.error("Erro ao buscar pacientes:", error);
        this.clearData();
      } finally {
        this.setLoading(false);
      }
    },
    // === FORMATAÇÃO DE DADOS ===
    formatUsersData(data, type) {
      if (!Array.isArray(data)) return [];
      return data.map((user) => {
        const primaryPhone = (user.phones || []).find(
          (phone) => phone.is_primary === 1 || phone.is_primary === true,
        );
        return {
          id: user.id,
          name: user.name,
          email: user.email || null,
          email_gmail: user.email_gmail || null,
          cpf: user.cpf || null,
          status: user.status || null,
          profile_image: user.profile_image,
          birth_date: user.birth_date,
          age: calculateAge(user.birth_date, { fallback: "Data inválida" }),
          birthdate: user.birth_date ? formatBirthDate(user.birth_date) : "N/A",
          gender: user.gender || "N/A",
          bloodType: user.bloodType || "N/A",
          specialty: type === "medico" ? user.specialty || "Médico" : undefined,
          crm: type === "medico" ? user.crm || null : undefined,
          phones: user.phones || [],
          address: user.address
            ? {
                id: user.address.id,
                street: user.address.street || null,
                number: user.address.number || null,
                neighborhood: user.address.neighborhood || null,
                city: user.address.city || null,
                state: user.address.state || null,
                cep: user.address.cep || null,
                created_at: user.address.created_at,
                updated_at: user.address.updated_at,
              }
            : null,
          allergies: user.allergies || null,
          medications: user.medications || null,
          medical_history: user.medical_history || null,
          rg: user.rg || null,
          observations: user.observations || null,
          // (mantive caso use em algum lugar)
          primaryPhone,
          // SALVIA-955: oculto da lista deste médico.
          hidden: Boolean(user.hidden),
        };
      });
    },
    isClinicUser() {
      return [USER_TYPES.CLINIC_DOCTOR, USER_TYPES.ASSISTANT].includes(
        this.userType,
      );
    },
    getDataType() {
      return this.viewMode === VIEW_MODES.DOCTORS ? "medico" : "paciente";
    },
    setLoading(value) {
      this.loading = value;
    },
    clearData() {
      this.patients = [];
      this.filteredItems = [];
    },
    handleError(message) {
      console.error(message);
    },
    handleScheduleAppointment(patientData) {
      this.AddNewEventPreSelectedPatient = patientData;
      this.openDialogAddNewEvent = true;
    },
    handleAddDependent(patient) {
      this.addNewUserDialog.visible = true;
      this.addNewUserDialog.userId = null;
      this.addNewUserDialog.targetType = "patient";
      this.addNewUserDialog.guardianId = patient.id;
      this.addNewUserDialog.guardianAddress = patient.address || null;
    },
  },
};
</script>
